import type { ReactNode } from "react";
import { EXPANDABLE_DIALOG_WITH_SIDEBAR_SUBSECTION_NAME } from "./constants";
import { LucideIcon } from "lucide-react";

export interface SubsectionProps {
  title: string;
  children: ReactNode;
  sectionKey?: string;
  trailingText?: string;
  actions?: Array<{
    icon: LucideIcon;
    onClick: () => void;
    label?: string;
  }>;
  scrollable?: boolean;
}

/**
 * Defines a subsection within an ExpandableDialogWithSidebar Section.
 *
 * Subsections provide hierarchical organization within sections. They do not appear
 * in the sidebar navigation - only top-level sections are shown there. Subsections
 * are typically navigated to via action buttons in the parent section's header.
 *
 * When a parent section contains both regular content and subsections, only the
 * regular content is shown when the parent is selected. If a parent has ONLY
 * subsections (no other content), the first subsection is automatically selected.
 *
 * @param title - Display name shown in the breadcrumb navigation when this subsection is active
 * @param children - React content to display when this subsection is active
 * @param icon - Lucide icon component (currently used for metadata, may be used in future UI enhancements)
 * @param key - Optional unique identifier for the subsection (defaults to title if not provided)
 * @param trailingText - Optional text to display after breadcrumbs in the header
 * @param actions - Optional array of action buttons to display in the header (icon buttons)
 *
 * @example
 * ```tsx
 * <ExpandableDialogWithSidebar.Section
 *   title="Contact"
 *   icon={UserIcon}
 *   actions={[{icon: ChevronRight, onClick: () => setSection('contact.details')}]}
 * >
 *   <div>Contact overview content</div>
 *   <ExpandableDialogWithSidebar.Subsection
 *     title="Details"
 *     icon={InfoIcon}
 *     trailingText="(Read-only)"
 *   >
 *     <div>Detailed contact information</div>
 *   </ExpandableDialogWithSidebar.Subsection>
 * </ExpandableDialogWithSidebar.Section>
 * ```
 */
export const Subsection = ({ children }: SubsectionProps) => {
  return <>{children}</>;
};
Subsection.displayName = EXPANDABLE_DIALOG_WITH_SIDEBAR_SUBSECTION_NAME;
